/**
 * useConfiguredWebSocket Hook
 *
 * Conecta ao WebSocket de pedidos usando as configurações do localStorage.
 */

import { useWebSocketOrders } from '@queries/order';
import type { UseWebSocketOrdersParams } from '@queries/order';
import { useFilaConfig } from './useFilaConfig';
import { generateWebSocketKey, validateConfigForRequest, configOrderTypeToApiChannel } from '../utils/config-to-api-params';

interface UseConfiguredWebSocketParams {
  currentIntervalId?: string;
  enabled?: boolean;
}

export function useConfiguredWebSocket({ currentIntervalId, enabled = true }: UseConfiguredWebSocketParams = {}) {
  const { config } = useFilaConfig();

  const validation = validateConfigForRequest(config);
  const isEnabled = enabled && validation.isValid && !!config.establishmentId;

  const webSocketKey = generateWebSocketKey(config, currentIntervalId);

  // O intervalo NÃO é enviado na conexão de propósito.
  //
  // O hub descarta, no servidor, todo evento cujo interval_id não bate com o
  // declarado pelo cliente. Como o intervalo corrente é resolvido a cada 10s e
  // a tela opera em "fora de intervalo" boa parte do tempo, declarar um
  // intervalo fazia eventos legítimos serem filtrados e sumirem — a fila só
  // atualizava na recarga periódica.
  //
  // Assinando o estabelecimento inteiro, o pedido que não está na lista
  // simplesmente não casa na sobreposição da tela, que já é filtrada pela API.
  const wsParams: UseWebSocketOrdersParams = {
    establishmentId: config.establishmentId || '',
    enabled: isEnabled,
  };

  const result = useWebSocketOrders(wsParams);

  return {
    ...result,
    webSocketKey,
    params: wsParams,
    validation,
    isConfigValid: validation.isValid,
    apiChannel: configOrderTypeToApiChannel(config.orderType),
  };
}
